<template>
  <div class="visual-edits-panel">
    <div class="visual-edits-content">
      <!-- Plot Section -->
      <div class="visual-edits-section">
        <div class="visual-edits-section-header">
        <h6 class="visual-edits-section-title">Plot</h6>
        </div>
        <div class="visual-edits-input-group">
          <!-- Plot Name -->
            <div class="visual-edits-input">
            <label class="visual-edits-label">Plot Name</label>
            <v-text-field
              v-model="plotName"
              variant="solo"
              class="custom-field"
              placeholder="Plot Name"
              hide-details
              outlined
              @update:modelValue="handlePlotNameChange"
            ></v-text-field>
            </div>

          <!-- Test Type -->
            <div class="visual-edits-input">
            <label class="visual-edits-label">Test Type</label>
            <v-select
              variant="solo"
              class="custom-field"
              v-model="selectedTableTypeName"
              :items="filteredTableTypesNames || []"
              item-title="title"
              item-value="value"
              placeholder="Test Type"
              @update:modelValue="tableTypeChange"
              :loading="loadingTableType"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="tableTypeSearchQuery"
                  placeholder="Test Type"
                  dense
                  outlined
                  clearable
                  @input="filterTableTypeList"
                  @click:clear="resetTableTypeList"
                  hide-details
                ></v-text-field>
                <div class="loader-container" v-if="loadingTableType">
                  <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
            </div>
              </template>
              <template #no-data>
                <v-list-item v-if="!loadingTableType">
                  <v-list-item-title>No data available</v-list-item-title>
                </v-list-item>
              </template>
              <template #selection>
                <span class="text-truncate">{{ selectedTableTypeName }}</span>
              </template>
            </v-select>
          </div>

          <!-- Plot Type -->
            <div class="visual-edits-input">
            <label class="visual-edits-label">Plot Type</label>
            <v-select
              variant="solo"
              class="custom-field"
              v-model="selectedChartTypeName"
              :items="filteredChartTypesNames || []"
              item-title="title"
              item-value="value"
              placeholder="Plot Type"
              @update:modelValue="chartTypeChange"
              :disabled="selectedTableTypeName === null"
              :loading="loadingChartType"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="chartTypeSearchQuery"
                  placeholder="Plot Type"
                  dense
                  outlined
                  clearable
                  @input="filterChartTypeList"
                  @click:clear="resetChartTypeList"
                  hide-details
                ></v-text-field>
                <div class="loader-container" v-if="loadingChartType">
                  <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                </div>
              </template>
              <template #selection>
                <span class="text-truncate">{{ selectedChartTypeName }}</span>
              </template>
            </v-select>
          </div>

          <!-- X-Axis Column -->
          <div class="visual-edits-input" v-if="showXAxis">
            <div class="d-flex align-center justify-space-between">
              <label class="visual-edits-label">X-Axis Column(s)</label>
              <div v-if="selectedChartTypeName === 'Line Plot' || selectedChartTypeName === 'Scatter Plot'" class="tab-div">
                <v-btn-toggle v-model="isXLog" class="axis-toggle" mandatory @update:modelValue="handleXScaleToggleChange">
                  <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                  <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                </v-btn-toggle>
              </div>
            </div>
            <v-select
              variant="solo"
              class="custom-field x-axis-select"
              v-model="selectedXAxisKey"
              :items="filteredXAxisItems || []"
              item-title="displayName"
              item-value="name"
              placeholder="X-Axis Column"
              @update:modelValue="handleXSelection"
              :loading="loadingXAxisData"
              :disabled="!selectedTableTypeName || loadingXAxisData"
              hide-details
            >
              <template #selection>
                <span class="text-truncate">{{ selectedXAxisDisplayName || selectedXAxisKey }}</span>
              </template>
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="xAxisSearchQuery"
                  placeholder="Search X-Axis Column"
                  dense
                  outlined
                  clearable
                  @input="filterXAxisList"
                  @click:clear="resetXAxisList"
                  hide-details
                ></v-text-field>
                <div class="loader-container" v-if="loadingXAxisData">
                  <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                </div>
              </template>
            </v-select>
          </div>

          <!-- Y-Axis Column(s) -->
          <div class="visual-edits-input" v-if="showYAxis">
            <div class="d-flex align-center justify-space-between">
              <label class="visual-edits-label">Y-Axis Column(s)</label>
              <div v-if="selectedChartTypeName === 'Line Plot' || selectedChartTypeName === 'Scatter Plot'" class="tab-div">
                <v-btn-toggle v-model="isYLog" class="axis-toggle" mandatory @update:modelValue="handleYScaleToggleChange">
                  <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                  <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                </v-btn-toggle>
              </div>
            </div>
            <v-select
              variant="solo"
              v-model="selectedYAxisKey"
              :items="filteredYAxisItems || []"
              multiple
              clearable
              placeholder="Y-Axis Column(s)"
              item-title="displayName"
              item-value="name"
              :return-object="false"
              @update:modelValue="handleYSelection"
              class="custom-y-axis-chips custom-field"
              :loading="loadingYAxisData"
              :disabled="!selectedTableTypeName || loadingYAxisData"
              hide-details
            >
              <template #selection="{ item, index }">
                <v-chip
                  v-if="index < 2"
                  closable
                  @click:close="removeYAxisItem(item.raw?.name || item.value || item.name)"
                  class="me-2"
                >
                  {{ item.title }}
                </v-chip>
                <span
                  v-else-if="index === 2"
                  class="text-grey text-caption align-self-center"
                >
                  (+{{ selectedYAxisKey.length - 2 }} others)
                </span>
              </template>
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="yAxisSearchQuery"
                  placeholder="Search Y-Axis Column"
                  dense
                  outlined
                  clearable
                  @input="filterYAxisList"
                  @click:clear="resetYAxisList"
                  hide-details
                ></v-text-field>
                <div class="loader-container" v-if="loadingYAxisData">
                  <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                </div>
              </template>
              <template #item="{ props, item }">
                <MultiselectCheckboxItem
                  :item-props="props"
                  :title="item.title"
                  :selected-values="selectedYAxisKey"
                  :item-value="item.value"
                />
              </template>
            </v-select>
          </div>

          <!-- Z-Axis Column -->
          <div class="visual-edits-input" v-if="showZAxis">
            <label class="visual-edits-label">Z-Axis Column</label>
            <v-select
              variant="solo"
              class="custom-field"
              v-model="selectedZAxisKey"
              placeholder="Z-Axis Column"
              :items="filteredZAxisItems || []"
              item-title="displayName"
              item-value="name"
              @update:modelValue="handleZSelection"
              :loading="loadingZAxisData"
              :disabled="!selectedTableTypeName || loadingZAxisData"
              hide-details
            >
              <template #selection>
                <span class="text-truncate">{{ selectedZAxisDisplayName || selectedZAxisKey }}</span>
              </template>
              <template #prepend-item>
                <v-text-field
                  v-model="zAxisSearchQuery"
                  placeholder="Search Z-Axis Column"
                  dense
                  outlined
                  clearable
                  @input="filterZAxisList"
                  @click:clear="resetZAxisList"
                  hide-details
                ></v-text-field>
                <div class="loader-container" v-if="loadingZAxisData">
                  <v-progress-circular indeterminate color="black" class="loaders"></v-progress-circular>
                </div>
              </template>
            </v-select>
          </div>

          <!-- Legend -->
          <div class="visual-edits-input" v-if="showColorGroup">
            <label class="visual-edits-label">Legend</label>
            <v-select
              variant="solo"
              class="custom-field"
              v-model="selectedColorGroupKey"
              clearable
              placeholder="Legend"
              :items="filteredColorGroupItems || []"
              item-title="displayName"
              item-value="name"
              @update:modelValue="handleColorGroupSelection"
              :disabled="!selectedTableTypeName"
              hide-details
            >
              <template #selection>
                <span class="text-truncate">{{ selectedColorGroupDisplayName || selectedColorGroupKey }}</span>
              </template>
              <template #prepend-item>
                <v-text-field
                  v-model="colorGroupSearchQuery"
                  placeholder="Search Legend"
                  dense
                  outlined
                  clearable
                  @input="filterColorGroupList"
                  @click:clear="resetColorGroupList"
                  hide-details
                ></v-text-field>
              </template>
            </v-select>
          </div>

          <!-- Number of X bins (Histogram / 2D Histogram) -->
          <div class="visual-edits-input" v-if="showXBinsInput">
            <label class="visual-edits-label">Number of X bins</label>
            <div class="visual-edits-bins-value">{{ xBinsAsNumber }}</div>
            <input
              type="range"
              class="visual-edits-bins-slider slider"
              min="2"
              max="100"
              step="1"
              v-model.number="xBins"
              @input="handleXBinsChange"
            />
          </div>

          <!-- Number of Y bins (2D Histogram) -->
          <div class="visual-edits-input" v-if="showYBinsInput">
            <label class="visual-edits-label">Number of Y bins</label>
            <div class="visual-edits-bins-value">{{ yBinsAsNumber }}</div>
            <input
              type="range"
              class="visual-edits-bins-slider slider"
              min="2"
              max="100"
              step="1"
              v-model.number="yBins"
              @input="handleYBinsChange"
            />
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="visual-edits-section">
        <div class="visual-edits-section-header">
        <h6 class="visual-edits-section-title">Filters</h6>
        </div>
        <div class="visual-edits-input-group">
          <!-- Fluid Type(s) -->
            <div class="visual-edits-input">
            <label class="visual-edits-label">Fluid Type(s)</label>
            <v-select
              variant="solo"
              v-model="selectedSampleKind"
              multiple
              chips
              clearable
              closable-chips
              placeholder="Fluid Type(s)"
              :items="filteredSampleKinds.map((fluidType) => fluidType.name)"
              :return-object="false"
              class="custom-y-axis-chips custom-field"
              @update:modelValue="onSelectSampleKind"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="sampleKindSearchQuery"
                  placeholder="Search Fluid Type(s)"
                  dense
                  outlined
                  clearable
                  @input="filterSampleKindList"
                  @click:clear="resetSampleKindList"
                  hide-details
                ></v-text-field>
              </template>
              <template #item="{ props, item }">
                <MultiselectCheckboxItem
                  :item-props="props"
                  :title="item.title"
                  :selected-values="selectedSampleKind"
                  :item-value="item.value"
                />
              </template>
            </v-select>
            </div>

          <!-- Sample Type(s) -->
            <div class="visual-edits-input">
            <label class="visual-edits-label">Sample Type(s)</label>
            <v-select
              variant="solo"
              v-model="selectedSampleType"
              multiple
              chips
              clearable
              closable-chips
              placeholder="Sample Type(s)"
              :items="filteredSampleTypes"
              item-title="name"
              item-value="name"
              :return-object="false"
              class="custom-y-axis-chips custom-field"
              @update:modelValue="onSelectSampleType"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="sampleTypeSearchQuery"
                  placeholder="Search Sample Type(s)"
                  dense
                  outlined
                  clearable
                  @input="filterSampleTypeList"
                  @click:clear="resetSampleTypeList"
                  hide-details
                ></v-text-field>
              </template>
              <template #item="{ props, item }">
                <MultiselectCheckboxItem
                  :item-props="props"
                  :title="item.title"
                  :selected-values="selectedSampleType"
                  :item-value="item.value"
                />
              </template>
            </v-select>
            </div>

          <!-- Fluid Classification(s) -->
          <div class="visual-edits-input">
            <label class="visual-edits-label">Fluid Classification(s)</label>
            <v-select
              variant="solo"
              v-model="selectedfluidClassification"
              multiple
              chips
              clearable
              closable-chips
              placeholder="Fluid Classification(s)"
              :items="filteredfluidClassifications"
              item-title="name"
              item-value="name"
              :return-object="false"
              class="custom-y-axis-chips custom-field"
              @update:modelValue="onSelectfluidClassification"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="fluidClassificationSearchQuery"
                  placeholder="Search Fluid Classification(s)"
                  dense
                  outlined
                  clearable
                  @input="filterfluidClassificationList"
                  @click:clear="resetfluidClassificationList"
                  hide-details
                ></v-text-field>
              </template>
              <template #item="{ props, item }">
                <MultiselectCheckboxItem
                  :item-props="props"
                  :title="item.title"
                  :selected-values="selectedfluidClassification"
                  :item-value="item.value"
                />
              </template>
            </v-select>
          </div>

          <!-- Asset(s) -->
          <div class="visual-edits-input">
            <label class="visual-edits-label">Asset(s)</label>
            <v-select
              variant="solo"
              v-model="selectedAsset"
              multiple
              chips
              clearable
              closable-chips
              placeholder="Assets"
              :items="filteredAssets.map((asset) => asset.name)"
              :return-object="false"
              class="custom-y-axis-chips custom-field"
              @update:modelValue="onSelectAsset"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="assetSearchQuery"
                  placeholder="Search Assets"
                  dense
                  outlined
                  clearable
                  @input="filterAssetList"
                  @click:clear="resetAssetList"
                  hide-details
                ></v-text-field>
              </template>
              <template #item="{ props, item }">
                <MultiselectCheckboxItem
                  :item-props="props"
                  :title="item.title"
                  :selected-values="selectedAsset"
                  :item-value="item.value"
                />
              </template>
            </v-select>
          </div>

          <!-- Field(s) -->
          <div class="visual-edits-input">
            <label class="visual-edits-label">Field(s)</label>
            <v-select
              variant="solo"
              v-model="selectedField"
              multiple
              chips
              clearable
              closable-chips
              placeholder="Fields"
              :items="filteredFields.map((field) => field.name)"
              :return-object="false"
              class="custom-y-axis-chips custom-field"
              @update:modelValue="onSelectField"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="fieldSearchQuery"
                  placeholder="Search Fields"
                  dense
                  outlined
                  clearable
                  @input="filterFieldList"
                  @click:clear="resetFieldList"
                  hide-details
                ></v-text-field>
              </template>
              <template #item="{ props, item }">
                <MultiselectCheckboxItem
                  :item-props="props"
                  :title="item.title"
                  :selected-values="selectedField"
                  :item-value="item.value"
                />
              </template>
            </v-select>
              </div>

          <!-- Well(s) -->
          <div class="visual-edits-input">
            <label class="visual-edits-label">Well(s)</label>
            <v-select
              variant="solo"
              :items="filteredWells.map((well) => well.name)"
              multiple
              chips
              clearable
              closable-chips
              placeholder="Well(s)"
              item-title="displayName"
              item-value="name"
              :return-object="false"
              class="custom-y-axis-chips custom-field"
              v-model="selectedWell"
              @update:modelValue="onSelectWell"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="wellSearchQuery"
                  placeholder="Search Well(s)"
                  dense
                  outlined
                  clearable
                  @input="filterWellList"
                  @click:clear="resetWellList"
                  hide-details
                ></v-text-field>
              </template>
              <template #item="{ props, item }">
                <MultiselectCheckboxItem
                  :item-props="props"
                  :title="item.title"
                  :selected-values="selectedWell"
                  :item-value="item.value"
                />
              </template>
            </v-select>
            </div>

          <!-- Reservoir(s) -->
          <div class="visual-edits-input">
            <label class="visual-edits-label">Reservoir(s)</label>
            <v-select
              variant="solo"
              v-model="selectedReservoir"
              :items="filteredReservoirs.map((reservoir) => reservoir.name)"
              multiple
              chips
              clearable
              closable-chips
              placeholder="Reservoirs"
              item-title="displayName"
              item-value="name"
              :return-object="false"
              class="custom-y-axis-chips custom-field"
              @update:modelValue="onSelectReservoir"
              hide-details
            >
              <template #prepend-item>
                <v-text-field
                  variant="solo"
                  class="custom-field mx-4"
                  v-model="reservoirSearchQuery"
                  placeholder="Search Reservoirs"
                  dense
                  outlined
                  clearable
                  @input="filterReservoirList"
                  @click:clear="resetReservoirList"
                  hide-details
                ></v-text-field>
              </template>
              <template #item="{ props, item }">
                <MultiselectCheckboxItem
                  :item-props="props"
                  :title="item.title"
                  :selected-values="selectedReservoir"
                  :item-value="item.value"
                />
              </template>
            </v-select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { getDisplayNameDashboard } from "@/utils/cache";
import MultiselectCheckboxItem from "@/components/layout/Common/MultiselectCheckboxItem.vue";

export default {
  name: "VisualEditsPanel",
  components: {
    MultiselectCheckboxItem,
  },
  props: {
    selectedChart: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      // Plot fields
      plotName: "",
      selectedTableTypeName: null,
      selectedChartTypeName: null,
      selectedXAxisKey: null,
      selectedYAxisKey: [],
      selectedZAxisKey: null,
      selectedColorGroupKey: null,
      isXLog: false,
      isYLog: false,
      xAxisScale: "linear",
      yAxisScale: "linear",
      
      // Display names for selected values
      selectedXAxisDisplayName: "",
      selectedYAxisDisplayNames: [],
      selectedZAxisDisplayName: "",
      selectedColorGroupDisplayName: "",

      // Filter fields
      selectedSampleKind: [],
      selectedSampleType: [],
      selectedfluidClassification: [],
      selectedAsset: [],
      selectedField: [],
      selectedWell: [],
      selectedReservoir: [],

      // Flag to track if initialization is complete (prevents API calls on initial load)
      isInitialized: false,

      // Options data (initialize as empty arrays to prevent undefined errors)
      tableTypes: [],
      tableTypesNames: [],
      chartTypes: [], // Array of chart type objects from API
      chartTypesNames: [], // Array of chart type name strings
      xAxisItems: [],
      yAxisItems: [],
      zAxisItems: [],
      colorGroupItems: [],
      sampleKinds: [],
      sampleTypes: [],
      fluidClassifications: [],
      assets: [],
      fields: [],
      wells: [],
      reservoirs: [],

      // Filtered options (initialize as empty arrays to prevent undefined errors)
      filteredTableTypesNames: [],
      filteredChartTypesNames: [],
      filteredXAxisItems: [],
      filteredYAxisItems: [],
      filteredZAxisItems: [],
      filteredColorGroupItems: [],
      
      // Debounce timer for real-time updates
      updatePlotTimer: null,
      filteredSampleKinds: [],
      filteredSampleTypes: [],
      filteredfluidClassifications: [],
      filteredAssets: [],
      filteredFields: [],
      filteredWells: [],
      filteredReservoirs: [],

      // Search queries
      tableTypeSearchQuery: "",
      chartTypeSearchQuery: "",
      xAxisSearchQuery: "",
      yAxisSearchQuery: "",
      zAxisSearchQuery: "",
      colorGroupSearchQuery: "",
      sampleKindSearchQuery: "",
      sampleTypeSearchQuery: "",
      fluidClassificationSearchQuery: "",
      assetSearchQuery: "",
      fieldSearchQuery: "",
      wellSearchQuery: "",
      reservoirSearchQuery: "",

      // Loading states
      loadingTableType: false,
      loadingChartType: false,
      loadingXAxisData: false,
      loadingYAxisData: false,
      loadingZAxisData: false,

      // Visibility
      showXAxis: true,
      showYAxis: true,
      showZAxis: false,
      showColorGroup: false,
      showXBinsInput: false,
      showYBinsInput: false,
      xBins: 20,
      yBins: 20,

      // Abort controllers
      tableTypeAbortController: null,
      dataFetchAbortController: null,
    };
  },
  computed: {
    tenantId() {
      return getTenantPrefix();
    },
    chartMetadata() {
      if (!this.selectedChart) {
        return null;
      }
      return this.selectedChart.data || this.selectedChart;
    },
    xBinsAsNumber() {
      return Number(this.xBins);
    },
    yBinsAsNumber() {
      return Number(this.yBins);
    },
  },
  watch: {
    selectedChart: {
      immediate: true,
      async handler(newVal, oldVal) {
        const newChartId = newVal?.data?.ID || newVal?.ID;
        const oldChartId = oldVal?.data?.ID || oldVal?.ID;

        // Skip re-init when the parent refreshed the same plot (e.g. after visual edit save)
        if (newChartId && oldChartId && newChartId === oldChartId && this.isInitialized) {
          return;
        }

        this.isInitialized = false;

        if (newVal && newVal.data) {
          if (!this.tableTypesNames || this.tableTypesNames.length === 0) {
            await this.getTableTypes();
          }
          await this.initializeFromChart();
        }
      },
    },
    // Real-time updates for plot fields - only after initialization
    plotName: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined && newVal !== oldVal) {
          this.debouncedUpdatePlot();
        }
      },
    },
    selectedTableTypeName: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== null && oldVal !== undefined && newVal !== oldVal) {
          this.debouncedUpdatePlot();
        }
      },
    },
    selectedChartTypeName: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== null && oldVal !== undefined && newVal !== oldVal) {
          this.debouncedUpdatePlot();
        }
      },
    },
    selectedXAxisKey: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
    selectedYAxisKey: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
    selectedZAxisKey: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== null && oldVal !== undefined && newVal !== oldVal) {
          this.debouncedUpdatePlot();
        }
      },
    },
    selectedColorGroupKey: {
      handler(newVal, oldVal) {
        if (!this.isInitialized || newVal === oldVal) {
          return;
        }
        this.debouncedUpdatePlot();
      },
    },
    xAxisScale: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined && newVal !== oldVal) {
          this.debouncedUpdatePlot();
        }
      },
    },
    yAxisScale: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined && newVal !== oldVal) {
          this.debouncedUpdatePlot();
        }
      },
    },
    // Real-time updates for filters - only after initialization
    selectedSampleKind: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
    selectedSampleType: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
    selectedfluidClassification: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
    selectedAsset: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
    selectedField: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
    selectedWell: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
    selectedReservoir: {
      handler(newVal, oldVal) {
        // Only update if initialization is complete and value actually changed
        if (this.isInitialized && oldVal !== undefined) {
          // Deep comparison for arrays
          const newValStr = JSON.stringify(newVal);
          const oldValStr = JSON.stringify(oldVal);
          if (newValStr !== oldValStr) {
            this.debouncedUpdatePlot();
          }
        }
      },
      deep: true,
    },
  },
  async mounted() {
    // Load table types first (needed for matching TableType from chart)
    await this.getTableTypes();
    await this.getChartTypes();
    // Load filter options
    await this.getAllSampleKinds();
    await this.getAllSampleTypes();
    await this.getAllfluidClassifications();
    await this.getAllAssets();
    await this.fetchField();
    await this.fetchReservoir();
    await this.fetchWell();
    // Initialize from chart after all options are loaded (especially table types)
    if (this.selectedChart) {
      await this.initializeFromChart();
    }
  },
  beforeUnmount() {
    // Cancel any pending requests
    if (this.tableTypeAbortController) {
      this.tableTypeAbortController.abort();
    }
    if (this.dataFetchAbortController) {
      this.dataFetchAbortController.abort();
    }
  },
  methods: {
    async initializeFromChart() {
      if (!this.selectedChart || !this.chartMetadata) {
        console.warn("VisualEditsPanel: Cannot initialize - missing chart data");
        return;
      }

      // Initialize plot fields
      this.plotName = this.chartMetadata.Name || "";
      
      // Handle TableType - it might be stored as table.Name or table.table
      // Try to find matching table type in our list
      const chartTableType = this.chartMetadata.TableType;
      if (chartTableType && this.tableTypesNames && this.tableTypesNames.length > 0) {
        // First try exact match
        let foundTableType = this.tableTypesNames.find(name => name === chartTableType);
        if (!foundTableType) {
          // Try case-insensitive match
          foundTableType = this.tableTypesNames.find(name => 
            String(name).toLowerCase() === String(chartTableType).toLowerCase()
          );
        }
        this.selectedTableTypeName = foundTableType || chartTableType;
      } else {
        this.selectedTableTypeName = chartTableType || null;
      }
      
      this.selectedChartTypeName = this.chartMetadata.ChartType || null;
      this.updateAxisVisibilityFromChartType(this.selectedChartTypeName, { preserveBins: true });
      if (this.chartMetadata.NumXBins != null && this.chartMetadata.NumXBins !== "") {
        this.xBins = Number(this.chartMetadata.NumXBins);
      }
      if (this.chartMetadata.NumYBins != null && this.chartMetadata.NumYBins !== "") {
        this.yBins = Number(this.chartMetadata.NumYBins);
      }
      this.selectedXAxisKey = this.chartMetadata.XAxisColumn || null;
      const groupColFromChart = this.chartMetadata.GroupColumn || null;
      let yAxisKeys = [];
      if (this.chartMetadata.YAxisColumn) {
        yAxisKeys = this.chartMetadata.YAxisColumn.split(",").map((col) => col.trim()).filter((col) => col);
      } else if (Array.isArray(this.chartMetadata.YAxisColumns)) {
        yAxisKeys = this.chartMetadata.YAxisColumns.map((col) => String(col).trim()).filter((col) => col);
      }
      if (groupColFromChart) {
        yAxisKeys = yAxisKeys.filter((col) => col !== groupColFromChart);
      }
      this.selectedYAxisKey = yAxisKeys;
      this.selectedZAxisKey = this.chartMetadata.ZAxisColumn || null;
      this.selectedColorGroupKey = this.chartMetadata.GroupColumn || null;
      this.isXLog = this.chartMetadata.XAxisScale === "log";
      this.isYLog = this.chartMetadata.YAxisScale === "log";
      this.xAxisScale = this.chartMetadata.XAxisScale || "linear";
      this.yAxisScale = this.chartMetadata.YAxisScale || "linear";

      // Initialize filter fields
      const filter = this.chartMetadata.Filter || {};
      this.selectedSampleKind = filter.fluidType || [];
      this.selectedSampleType = filter.sampleType || [];
      this.selectedfluidClassification = filter.fluidClassification || [];
      this.selectedAsset = filter.asset || [];
      this.selectedField = filter.field || [];
      this.selectedWell = filter.well || [];
      this.selectedReservoir = filter.reservoir || [];

      // Merge selected filter values with loaded options (so they appear in dropdowns)
      // This ensures selected values are visible even if they're not in the current API response
      const mergeOptions = (existingOptions, selectedValues, hasDisplayName = false) => {
        const selectedOptions = (selectedValues || []).map(name => 
          hasDisplayName ? { name, displayName: name } : { name }
        );
        // Combine and deduplicate
        const combined = [...existingOptions, ...selectedOptions];
        const unique = combined.filter((item, index, self) =>
          index === self.findIndex((t) => t.name === item.name)
        );
        return this.sortAlphabetically(unique);
      };

      this.sampleKinds = mergeOptions(this.sampleKinds, filter.fluidType);
      this.sampleTypes = mergeOptions(this.sampleTypes, filter.sampleType);
      this.fluidClassifications = mergeOptions(this.fluidClassifications, filter.fluidClassification);
      this.assets = mergeOptions(this.assets, filter.asset);
      this.fields = mergeOptions(this.fields, filter.field);
      this.wells = mergeOptions(this.wells, filter.well, true);
      this.reservoirs = mergeOptions(this.reservoirs, filter.reservoir, true);

      // Set filtered arrays
      this.filteredSampleKinds = this.sampleKinds;
      this.filteredSampleTypes = this.sampleTypes;
      this.filteredfluidClassifications = this.fluidClassifications;
      this.filteredAssets = this.assets;
      this.filteredFields = this.fields;
      this.filteredWells = this.wells;
      this.filteredReservoirs = this.reservoirs;

      // Load columns if table type is set
      if (this.selectedTableTypeName) {
        // Use async/await to ensure columns are loaded before continuing
        await this.loadColumnsForTableType(this.selectedTableTypeName);
        // Fetch display names for selected values after columns are loaded
        await this.fetchDisplayNamesForSelections();
      } else {
        // Still fetch display names if we have selections
        await this.fetchDisplayNamesForSelections();
      }

      // Mark initialization as complete - now user changes will trigger updates
      this.isInitialized = true;
    },
    async fetchDisplayNamesForSelections() {
      if (!this.selectedTableTypeName) return;

      try {
        // Fetch X-axis display name
        if (this.selectedXAxisKey) {
          const xDisplayName = await getDisplayNameDashboard(
            this.selectedTableTypeName,
            this.selectedXAxisKey
          );
          this.selectedXAxisDisplayName = xDisplayName.displayName;
        }

        // Fetch Y-axis display names
        if (this.selectedYAxisKey && this.selectedYAxisKey.length > 0) {
          const yDisplayNames = await Promise.all(
            this.selectedYAxisKey.map((column) =>
              getDisplayNameDashboard(this.selectedTableTypeName, column)
            )
          );
          this.selectedYAxisDisplayNames = yDisplayNames.map((item) => item.displayName);
        }

        // Fetch Z-axis display name
        if (this.selectedZAxisKey) {
          const zDisplayName = await getDisplayNameDashboard(
            this.selectedTableTypeName,
            this.selectedZAxisKey
          );
          this.selectedZAxisDisplayName = zDisplayName.displayName;
        }

        // Fetch Color Group/Legend display name
        if (this.selectedColorGroupKey) {
          const groupColumn = this.selectedColorGroupKey;
          const capitalizedGroupColumn = groupColumn.charAt(0).toUpperCase() + groupColumn.slice(1);
          const colorGroupDisplayName = await getDisplayNameDashboard(
            this.selectedTableTypeName,
            capitalizedGroupColumn
          );
          this.selectedColorGroupDisplayName = colorGroupDisplayName.displayName;
        }
      } catch (error) {
        console.error("Error fetching display names:", error);
      }
    },
    async getTableTypes() {
      this.loadingTableType = true;
      try {
        const response = await api.get("public/api/tables/type", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.tableTypes = response.data.data.tableTypes;
          // Extract name from table.table property (same as studio)
          this.tableTypesNames = response.data.data.tableTypes.map((table) => table.table);
          this.filteredTableTypesNames = this.tableTypesNames.map((name) => ({
            title: name,
            value: name,
          }));
        }
      } catch (error) {
        console.error("Error fetching table types:", error);
      } finally {
        this.loadingTableType = false;
      }
    },
    async getChartTypes() {
      this.loadingChartType = true;
      try {
        const response = await api.get("public/api/charts/type", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          const chartTypesData = response.data.data.chart_types || [];
          this.chartTypes = Array.isArray(chartTypesData) ? chartTypesData : [];
          this.chartTypesNames = this.chartTypes
            .filter((chart) => chart && chart.Name)
            .map((chart) => chart.Name);
          this.filteredChartTypesNames = this.chartTypesNames.map((name) => ({
            title: name,
            value: name,
          }));
        } else {
          // Initialize as empty arrays if API call fails
          this.chartTypes = [];
          this.chartTypesNames = [];
          this.filteredChartTypesNames = [];
        }
      } catch (error) {
        console.error("Error fetching chart types:", error);
      } finally {
        this.loadingChartType = false;
      }
    },
    async loadColumnsForTableType(tableType) {
      if (!tableType) return;

      this.loadingXAxisData = true;
      this.loadingYAxisData = true;
      this.loadingZAxisData = true;

      // Cancel previous request
      if (this.tableTypeAbortController) {
        this.tableTypeAbortController.abort();
      }
      this.tableTypeAbortController = new AbortController();

      try {
        // JSON stringify the table type first, then encode it (same format as studio)
        const tableTypeString = JSON.stringify(tableType);
        const response = await api.get(
          `public/api/tables/type?table=${encodeURIComponent(tableTypeString)}`,
          {
            signal: this.tableTypeAbortController.signal,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );

        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          const columns = response.data.data.tableTypes.columns.filter((column) => column.displayName);
          columns.sort((a, b) => (a.columnSortOrder || 999) - (b.columnSortOrder || 999));

          // Filter columns by canChart property (same as studio)
          // X, Y, Z axis items should have canChart property
          this.xAxisItems = columns
            .filter((col) => col.canChart)
            .map((column) => ({
              displayName: column.displayName,
              name: column.name,
              sortOrder: column.sortOrder || 999,
            }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
            
          // Keep full Y-axis list; when a legend is selected, we remove that exact column from Y-axis selection/options.
          this.yAxisItems = columns
            .filter((col) => col.canChart)
            .map((column) => ({
              displayName: column.displayName,
              name: column.name,
              sortOrder: column.sortOrder || 999,
            }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
            
          this.zAxisItems = columns
            .filter((col) => col.canChart)
            .map((column) => ({
              displayName: column.displayName,
              name: column.name,
              sortOrder: column.sortOrder || 999,
            }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
            
          const explicitLegendColumns = columns.filter(
            (column) => column.legend === true || column.isLegend === true || column.canLegend === true
          );
          const legendSourceColumns = explicitLegendColumns.length > 0
            ? explicitLegendColumns
            : columns.filter((column) => column.canChart);

          this.colorGroupItems = legendSourceColumns
            .map((column) => ({
              displayName: column.displayName,
              name: column.name,
              sortOrder: column.legendSortOrder || 999,
            }))
            .sort((a, b) => a.sortOrder - b.sortOrder);

          // Ensure all items are valid objects (filter out any undefined/null items)
          this.filteredXAxisItems = (this.xAxisItems || []).filter(item => item && typeof item === 'object');
          this.filteredYAxisItems = (this.yAxisItems || []).filter(item => item && typeof item === 'object');
          this.filteredZAxisItems = (this.zAxisItems || []).filter(item => item && typeof item === 'object');
          this.filteredColorGroupItems = (this.colorGroupItems || []).filter(item => item && typeof item === 'object');

          // If chart already has a legend column but API list didn't include it, keep it visible/selectable.
          if (
            this.selectedColorGroupKey &&
            !this.colorGroupItems.some((item) => item && item.name === this.selectedColorGroupKey)
          ) {
            this.colorGroupItems = [
              {
                displayName: this.selectedColorGroupDisplayName || this.selectedColorGroupKey,
                name: this.selectedColorGroupKey,
                sortOrder: -1,
              },
              ...this.colorGroupItems,
            ];
            this.filteredColorGroupItems = this.colorGroupItems;
          }

          await this.applyLegendToYAxisFilter();
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error loading columns:", error);
        }
      } finally {
        this.loadingXAxisData = false;
        this.loadingYAxisData = false;
        this.loadingZAxisData = false;
      }
    },
    async tableTypeChange(tableType) {
      // Clear previous selections when test type changes
      this.selectedXAxisKey = null;
      this.selectedYAxisKey = [];
      this.selectedZAxisKey = null;
      this.selectedColorGroupKey = null;
      this.selectedXAxisDisplayName = "";
      this.selectedYAxisDisplayNames = [];
      this.selectedZAxisDisplayName = "";
      this.selectedColorGroupDisplayName = "";
      
      // Clear axis items
      this.xAxisItems = [];
      this.yAxisItems = [];
      this.zAxisItems = [];
      this.colorGroupItems = [];
      this.filteredXAxisItems = [];
      this.filteredYAxisItems = [];
      this.filteredZAxisItems = [];
      this.filteredColorGroupItems = [];
      
      this.selectedTableTypeName = tableType;
      
      // Load columns for the new table type
      await this.loadColumnsForTableType(tableType);
      
      // Fetch display names after columns are loaded (if there are any selections)
      await this.fetchDisplayNamesForSelections();
      
      // Debounced update will be triggered by watcher
    },
    updateAxisVisibilityFromChartType(chartType, options = {}) {
      // Default visibility settings
      this.showXAxis = true;
      this.showYAxis = true;
      this.showZAxis = false;
      this.showColorGroup = false;
      
      // Try to find chart type object and update visibility
      if (this.chartTypes && Array.isArray(this.chartTypes) && this.chartTypes.length > 0) {
        const chartTypeObj = this.getChartTypeObject(chartType);
        if (chartTypeObj) {
          // Safely check properties with optional chaining
          if (chartTypeObj.xAxis !== undefined) {
            this.showXAxis = chartTypeObj.xAxis !== false;
          }
          if (chartTypeObj.yAxis !== undefined) {
            this.showYAxis = chartTypeObj.yAxis !== false;
          }
          if (chartTypeObj.zAxis !== undefined) {
            this.showZAxis = chartTypeObj.zAxis === true;
          }
          if (chartTypeObj.legend !== undefined || chartTypeObj.Legend !== undefined) {
            this.showColorGroup =
              chartTypeObj.legend === true ||
              chartTypeObj.Legend === true ||
              chartTypeObj.legend === "true" ||
              chartTypeObj.Legend === "true";
          }
        } else {
          // If chart type not found, set defaults based on common chart types
          if (chartType === "3D Scatter" || chartType === "3D Plot") {
            this.showZAxis = true;
          }
          if (
            chartType === "Heatmap" ||
            chartType === "Contour Plot" ||
            chartType === "Scatter Plot" ||
            chartType === "Line Plot" ||
            chartType === "Bar Chart" ||
            chartType === "Bubble"
          ) {
            this.showColorGroup = true;
          }
        }
      }
      if ((this.colorGroupItems || []).length > 0) {
        this.showColorGroup = true;
      }

      this.updateBinsVisibilityFromChartType(chartType, options);
    },
    getChartTypeObject(chartType) {
      if (!chartType || !Array.isArray(this.chartTypes)) return null;
      return (
        this.chartTypes.find(
          (ct) => ct && (ct.Name === chartType || ct.name === chartType)
        ) || null
      );
    },
    updateBinsVisibilityFromChartType(chartType, options = {}) {
      const preserveBins = options.preserveBins === true;
      const chartTypeObj = this.getChartTypeObject(chartType);

      let showXBins = false;
      let showYBins = false;

      if (chartTypeObj) {
        showXBins = !!chartTypeObj.has_x_bins;
        showYBins = !!chartTypeObj.has_y_bins;
      } else if (chartType === "Histogram") {
        showXBins = true;
      } else if (chartType === "2D Histogram") {
        showXBins = true;
        showYBins = true;
      }

      this.showXBinsInput = showXBins;
      this.showYBinsInput = showYBins;

      if (!preserveBins) {
        if (showXBins) {
          this.xBins = chartType === "Histogram" ? 10 : 20;
        }
        if (showYBins) {
          this.yBins = 20;
        }
      }
    },
    chartTypeChange(chartType) {
      this.selectedChartTypeName = chartType;
      this.updateAxisVisibilityFromChartType(chartType);
      // Debounced update will be triggered by watcher
    },
    async handleXSelection(xAxis) {
      this.selectedXAxisKey = xAxis;
      // Fetch display name for selected X-axis
      if (xAxis && this.selectedTableTypeName) {
        try {
          const xDisplayName = await getDisplayNameDashboard(
            this.selectedTableTypeName,
            xAxis
          );
          this.selectedXAxisDisplayName = xDisplayName.displayName;
        } catch (error) {
          console.error("Error fetching X-axis display name:", error);
        }
      }
      // Debounced update will be triggered by watcher
    },
    async handleYSelection(yAxis) {
      const blockedLegend = this.selectedColorGroupKey;
      const rawSelection = Array.isArray(yAxis) ? yAxis : [yAxis];
      this.selectedYAxisKey = rawSelection.filter((column) => column && column !== blockedLegend);
      // Fetch display names for selected Y-axis columns
      if (this.selectedYAxisKey.length > 0 && this.selectedTableTypeName) {
        try {
          const yDisplayNames = await Promise.all(
            this.selectedYAxisKey.map((column) =>
              getDisplayNameDashboard(this.selectedTableTypeName, column)
            )
          );
          this.selectedYAxisDisplayNames = yDisplayNames.map((item) => item.displayName);
        } catch (error) {
          console.error("Error fetching Y-axis display names:", error);
        }
      }
      // Debounced update will be triggered by watcher
    },
    async handleZSelection(zAxis) {
      this.selectedZAxisKey = zAxis;
      // Fetch display name for selected Z-axis
      if (zAxis && this.selectedTableTypeName) {
        try {
          const zDisplayName = await getDisplayNameDashboard(
            this.selectedTableTypeName,
            zAxis
          );
          this.selectedZAxisDisplayName = zDisplayName.displayName;
        } catch (error) {
          console.error("Error fetching Z-axis display name:", error);
        }
      }
      // Debounced update will be triggered by watcher
    },
    async handleColorGroupSelection(colorGroup) {
      this.selectedColorGroupKey = colorGroup;
      await this.applyLegendToYAxisFilter();
      // Fetch display name for selected Color Group/Legend
      if (colorGroup && this.selectedTableTypeName) {
        try {
          const capitalizedGroupColumn = colorGroup.charAt(0).toUpperCase() + colorGroup.slice(1);
          const colorGroupDisplayName = await getDisplayNameDashboard(
            this.selectedTableTypeName,
            capitalizedGroupColumn
          );
          this.selectedColorGroupDisplayName = colorGroupDisplayName.displayName;
        } catch (error) {
          console.error("Error fetching Color Group display name:", error);
        }
      } else {
        this.selectedColorGroupDisplayName = "";
      }
      this.debouncedUpdatePlot();
    },
    handleXScaleToggleChange(isLog) {
      this.isXLog = isLog;
      this.xAxisScale = isLog ? "log" : "linear";
      // Debounced update will be triggered by watcher
    },
    handleYScaleToggleChange(isLog) {
      this.isYLog = isLog;
      this.yAxisScale = isLog ? "log" : "linear";
      // Debounced update will be triggered by watcher
    },
    handleXBinsChange() {
      if (this.isInitialized) {
        this.debouncedUpdatePlot();
      }
    },
    handleYBinsChange() {
      if (this.isInitialized) {
        this.debouncedUpdatePlot();
      }
    },
    handlePlotNameChange(name) {
      this.plotName = name;
      // Don't update plot immediately for name changes
    },
    onSelectSampleKind(sampleKind) {
      this.selectedSampleKind = Array.isArray(sampleKind) ? sampleKind : [sampleKind];
      // Debounced update will be triggered by watcher
    },
    onSelectSampleType(sampleType) {
      this.selectedSampleType = Array.isArray(sampleType) ? sampleType : [sampleType];
      // Debounced update will be triggered by watcher
    },
    onSelectfluidClassification(fluidClassification) {
      this.selectedfluidClassification = Array.isArray(fluidClassification) ? fluidClassification : [fluidClassification];
      // Debounced update will be triggered by watcher
    },
    async onSelectAsset(asset) {
      this.selectedAsset = Array.isArray(asset) ? asset : [asset];
      // Reload dependent dropdowns
      await this.fetchField([], this.selectedAsset);
      await this.fetchReservoir([], this.selectedAsset, []);
      await this.fetchWell([], this.selectedAsset, [], []);
      // Debounced update will be triggered by watcher
    },
    async onSelectField(field) {
      this.selectedField = Array.isArray(field) ? field : [field];
      // Reload dependent dropdowns
      await this.fetchReservoir([], this.selectedAsset, this.selectedField);
      await this.fetchWell([], this.selectedAsset, this.selectedField, []);
      // Debounced update will be triggered by watcher
    },
    async onSelectReservoir(reservoir) {
      this.selectedReservoir = Array.isArray(reservoir) ? reservoir : [reservoir];
      // Reload dependent dropdowns
      await this.fetchWell([], this.selectedAsset, this.selectedField, this.selectedReservoir);
      // Debounced update will be triggered by watcher
    },
    onSelectWell(well) {
      this.selectedWell = Array.isArray(well) ? well : [well];
      // Debounced update will be triggered by watcher
    },
    debouncedUpdatePlot() {
      // Clear existing timer
      if (this.updatePlotTimer) {
        clearTimeout(this.updatePlotTimer);
      }
      
      // Set new timer to update plot after 300ms delay
      this.updatePlotTimer = setTimeout(() => {
        this.updatePlot();
      }, 300);
    },
    getResolvedYAxisColumnString() {
      const groupCol = this.selectedColorGroupKey;
      return (Array.isArray(this.selectedYAxisKey) ? this.selectedYAxisKey : [])
        .filter((col) => col && col !== groupCol)
        .join(",");
    },
    getResolvedYAxisColumnsArray() {
      const yCols = (Array.isArray(this.selectedYAxisKey) ? this.selectedYAxisKey : []).filter(Boolean);
      const groupCol = this.selectedColorGroupKey;
      const result = [...yCols];
      if (groupCol && !result.includes(groupCol)) {
        result.push(groupCol);
      }
      return result;
    },
    async updatePlot() {
      // Emit event to parent to update the plot
      // Build filter object same as studio - preserve existing filter structure and update with current selections
      const existingFilter = this.chartMetadata?.Filter || {};
      
      // Build filter object with same structure as studio's saveFilterWithChart
      // Always use current UI selections (even if empty) to allow users to clear filters
      // Only preserve fields that are not editable in the UI (like ID, name, sampleDate, etc.)
      const filter = {
        ID: existingFilter.ID || null,
        name: existingFilter.name || "",
        // Always use current UI selections - if user cleared them, they should be empty
        asset: Array.isArray(this.selectedAsset) ? this.selectedAsset : [],
        field: Array.isArray(this.selectedField) ? this.selectedField : [],
        reservoir: Array.isArray(this.selectedReservoir) ? this.selectedReservoir : [],
        well: Array.isArray(this.selectedWell) ? this.selectedWell : [],
        sampleDate: existingFilter.sampleDate || null, // Not editable in UI, preserve existing
        fluidType: Array.isArray(this.selectedSampleKind) ? this.selectedSampleKind : [],
        sampleType: Array.isArray(this.selectedSampleType) ? this.selectedSampleType : [],
        fluidClassification: Array.isArray(this.selectedfluidClassification) ? this.selectedfluidClassification : [],
        fluidSampleID: existingFilter.fluidSampleID || [], // Not editable in UI, preserve existing
        fluidSampleContainerID: existingFilter.fluidSampleContainerID || [], // Not editable in UI, preserve existing
        source: existingFilter.source || "", // Not editable in UI, preserve existing
        user: existingFilter.user || "", // Not editable in UI, preserve existing
        public: existingFilter.public !== undefined ? existingFilter.public : true,
      };
      
      const shouldClearYAxisForHistogram =
        this.selectedChartTypeName === "Histogram" &&
        (!Array.isArray(this.selectedYAxisKey) || this.selectedYAxisKey.length === 0);

      const resolvedYAxisColumn = shouldClearYAxisForHistogram
        ? ""
        : this.getResolvedYAxisColumnString();
      const resolvedYAxisColumns = shouldClearYAxisForHistogram
        ? []
        : this.getResolvedYAxisColumnsArray();

      const updateData = {
        Name: this.plotName,
        TableType: this.selectedTableTypeName,
        ChartType: this.selectedChartTypeName,
        XAxisColumn: this.selectedXAxisKey,
        YAxisColumn: resolvedYAxisColumn,
        YAxisColumns: resolvedYAxisColumns,
        ZAxisColumn: this.selectedZAxisKey,
        GroupColumn: this.selectedColorGroupKey || "",
        XAxisScale: this.xAxisScale,
        YAxisScale: this.yAxisScale,
        Filter: filter,
      };

      if (this.showXBinsInput) {
        updateData.NumXBins = this.xBinsAsNumber;
      }
      if (this.showYBinsInput) {
        updateData.NumYBins = this.yBinsAsNumber;
      }
      
      console.log("VisualEditsPanel: Emitting plot-updated", {
        updateData,
        selectedYAxisKey: this.selectedYAxisKey,
        hasYAxis: !!(updateData.YAxisColumn)
      });
      
      this.$emit("plot-updated", updateData);
    },
    // Filter methods
    filterTableTypeList() {
      const query = this.tableTypeSearchQuery?.toLowerCase() || "";
      this.filteredTableTypesNames = this.tableTypesNames
        .filter((name) => name.toLowerCase().includes(query))
        .map((name) => ({ title: name, value: name }));
    },
    resetTableTypeList() {
      this.tableTypeSearchQuery = "";
      this.filterTableTypeList();
    },
    filterChartTypeList() {
      const query = this.chartTypeSearchQuery?.toLowerCase() || "";
      this.filteredChartTypesNames = this.chartTypesNames
        .filter((name) => name.toLowerCase().includes(query))
        .map((name) => ({ title: name, value: name }));
    },
    resetChartTypeList() {
      this.chartTypeSearchQuery = "";
      this.filterChartTypeList();
    },
    filterXAxisList() {
      const query = this.xAxisSearchQuery?.toLowerCase() || "";
      this.filteredXAxisItems = (this.xAxisItems || []).filter(
        (item) => item?.displayName?.toLowerCase().includes(query) || item?.name?.toLowerCase().includes(query)
      );
    },
    resetXAxisList() {
      this.xAxisSearchQuery = "";
      this.filterXAxisList();
    },
    filterYAxisList() {
      const query = this.yAxisSearchQuery?.toLowerCase() || "";
      const blockedLegend = this.selectedColorGroupKey;
      this.filteredYAxisItems = (this.yAxisItems || []).filter(
        (item) =>
          item &&
          item.name !== blockedLegend &&
          (item?.displayName?.toLowerCase().includes(query) || item?.name?.toLowerCase().includes(query))
      );
    },
    resetYAxisList() {
      this.yAxisSearchQuery = "";
      this.filterYAxisList();
    },
    async applyLegendToYAxisFilter() {
      const blockedLegend = this.selectedColorGroupKey;
      this.filteredYAxisItems = (this.yAxisItems || []).filter(
        (item) => item && item.name !== blockedLegend
      );

      if (!blockedLegend || !Array.isArray(this.selectedYAxisKey)) {
        return;
      }

      const nextY = this.selectedYAxisKey.filter((col) => col !== blockedLegend);
      if (nextY.length === this.selectedYAxisKey.length) {
        return;
      }
      this.selectedYAxisKey = nextY;

      if (nextY.length > 0 && this.selectedTableTypeName) {
        try {
          const yDisplayNames = await Promise.all(
            nextY.map((column) => getDisplayNameDashboard(this.selectedTableTypeName, column))
          );
          this.selectedYAxisDisplayNames = yDisplayNames.map((item) => item.displayName);
        } catch (error) {
          console.error("Error updating Y-axis display names after legend change:", error);
        }
      } else {
        this.selectedYAxisDisplayNames = [];
      }
    },
    filterZAxisList() {
      const query = this.zAxisSearchQuery?.toLowerCase() || "";
      this.filteredZAxisItems = (this.zAxisItems || []).filter(
        (item) => item?.displayName?.toLowerCase().includes(query) || item?.name?.toLowerCase().includes(query)
      );
    },
    resetZAxisList() {
      this.zAxisSearchQuery = "";
      this.filterZAxisList();
    },
    filterColorGroupList() {
      const query = this.colorGroupSearchQuery?.toLowerCase() || "";
      this.filteredColorGroupItems = (this.colorGroupItems || []).filter(
        (item) => item?.displayName?.toLowerCase().includes(query) || item?.name?.toLowerCase().includes(query)
      );
    },
    resetColorGroupList() {
      this.colorGroupSearchQuery = "";
      this.filterColorGroupList();
    },
    filterSampleKindList() {
      const query = this.sampleKindSearchQuery?.toLowerCase() || "";
      this.filteredSampleKinds = this.sampleKinds.filter((item) => item.name?.toLowerCase().includes(query));
    },
    resetSampleKindList() {
      this.sampleKindSearchQuery = "";
      this.filterSampleKindList();
    },
    filterSampleTypeList() {
      const query = this.sampleTypeSearchQuery?.toLowerCase() || "";
      this.filteredSampleTypes = this.sampleTypes.filter((item) => item.name?.toLowerCase().includes(query));
    },
    resetSampleTypeList() {
      this.sampleTypeSearchQuery = "";
      this.filterSampleTypeList();
    },
    filterfluidClassificationList() {
      const query = this.fluidClassificationSearchQuery?.toLowerCase() || "";
      this.filteredfluidClassifications = this.fluidClassifications.filter((item) => item.name?.toLowerCase().includes(query));
    },
    resetfluidClassificationList() {
      this.fluidClassificationSearchQuery = "";
      this.filterfluidClassificationList();
    },
    filterAssetList() {
      const query = this.assetSearchQuery?.toLowerCase() || "";
      this.filteredAssets = this.assets.filter((item) => item.name?.toLowerCase().includes(query));
    },
    resetAssetList() {
      this.assetSearchQuery = "";
      this.filterAssetList();
    },
    filterFieldList() {
      const query = this.fieldSearchQuery?.toLowerCase() || "";
      this.filteredFields = this.fields.filter((item) => item.name?.toLowerCase().includes(query));
    },
    resetFieldList() {
      this.fieldSearchQuery = "";
      this.filterFieldList();
    },
    filterWellList() {
      const query = this.wellSearchQuery?.toLowerCase() || "";
      this.filteredWells = this.wells.filter((item) => item.name?.toLowerCase().includes(query));
    },
    resetWellList() {
      this.wellSearchQuery = "";
      this.filterWellList();
    },
    filterReservoirList() {
      const query = this.reservoirSearchQuery?.toLowerCase() || "";
      this.filteredReservoirs = this.reservoirs.filter((item) => item.name?.toLowerCase().includes(query));
    },
    resetReservoirList() {
      this.reservoirSearchQuery = "";
      this.filterReservoirList();
    },
    async getAllSampleKinds() {
      try {
        const response = await api.get("public/api/summary/fluidType", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.sampleKinds = this.sortAlphabetically(
            response.data.data.columnSummary.filter((fluidType) => fluidType.name)
          );
          this.filteredSampleKinds = this.sampleKinds;
        }
      } catch (error) {
        console.error("Error fetching sample kinds:", error);
      }
    },
    async getAllSampleTypes() {
      try {
        const response = await api.get("public/api/unique-values/sampleType", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.sampleTypes = this.sortAlphabetically(
            response.data.data.columnUniqueValues.Values.map((value) => ({ name: value }))
          );
          this.filteredSampleTypes = this.sampleTypes;
        }
      } catch (error) {
        console.error("Error fetching sample types:", error);
      }
    },
    async getAllfluidClassifications() {
      try {
        const response = await api.get("public/api/unique-values/fluidClassification", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.fluidClassifications = this.sortAlphabetically(
            response.data.data.columnUniqueValues.Values.map((value) => ({ name: value }))
          );
          this.filteredfluidClassifications = this.fluidClassifications;
        }
      } catch (error) {
        console.error("Error fetching fluid classifications:", error);
      }
    },
    async getAllAssets(selectedSampleIDs = []) {
      try {
        let url = "public/api/locations/assets";
        if (selectedSampleIDs && selectedSampleIDs.length > 0) {
          url += `?fluidSampleID=${JSON.stringify(selectedSampleIDs)}`;
        }
        const response = await api.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.assets = this.sortAlphabetically(
            response.data.data.locations.filter((asset) => asset.name)
          );
          this.filteredAssets = this.assets;
        }
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    },
    async fetchField(selectedSampleIDs = [], selectedAssets = []) {
      try {
        const assetString = JSON.stringify(selectedAssets);
        const sampleIDString = JSON.stringify(selectedSampleIDs);
        const response = await api.get(
          `public/api/locations/fields?asset=${encodeURIComponent(assetString)}&fluidSampleID=${encodeURIComponent(sampleIDString)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.fields = this.sortAlphabetically(
            response.data.data.locations.filter((field) => field.name)
          );
          this.filteredFields = this.fields;
        }
      } catch (error) {
        console.error("Error fetching fields:", error);
      }
    },
    async fetchReservoir(selectedSampleIDs = [], selectedAssets = [], selectedFields = []) {
      try {
        const assetString = JSON.stringify(selectedAssets);
        const fieldString = JSON.stringify(selectedFields);
        const sampleIDString = JSON.stringify(selectedSampleIDs);
        const response = await api.get(
          `public/api/locations/reservoirs?asset=${encodeURIComponent(
            assetString
          )}&field=${encodeURIComponent(fieldString)}&fluidSampleID=${encodeURIComponent(sampleIDString)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.reservoirs = this.sortAlphabetically(
            response.data.data.locations.filter((reservoir) => reservoir.name)
          );
          this.filteredReservoirs = this.reservoirs;
        }
      } catch (error) {
        console.error("Error fetching reservoirs:", error);
      }
    },
    async fetchWell(selectedSampleIDs = [], selectedAssets = [], selectedFields = [], selectedReservoirs = []) {
      try {
        const assetString = JSON.stringify(selectedAssets);
        const fieldString = JSON.stringify(selectedFields);
        const reservoirString = JSON.stringify(selectedReservoirs);
        const sampleIDString = JSON.stringify(selectedSampleIDs);
        const response = await api.get(
          `public/api/locations/wells?asset=${encodeURIComponent(
            assetString
          )}&field=${encodeURIComponent(fieldString)}&reservoir=${encodeURIComponent(
            reservoirString
          )}&fluidSampleID=${encodeURIComponent(sampleIDString)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );
        if (response && response.data && (response.data.statusCode === 200 || response.data.statusCode === 201)) {
          this.wells = this.sortAlphabetically(
            response.data.data.locations.filter((well) => well.name)
          );
          this.filteredWells = this.wells;
        }
      } catch (error) {
        console.error("Error fetching wells:", error);
      }
    },
    sortAlphabetically(items) {
      return items.sort((a, b) => {
        const nameA = a.name ? a.name.toLowerCase() : "";
        const nameB = b.name ? b.name.toLowerCase() : "";
        return nameA.localeCompare(nameB);
      });
    },
    getYAxisDisplayName(columnName) {
      // Find the index of the column name in selectedYAxisKey
      const index = this.selectedYAxisKey.indexOf(columnName);
      if (index >= 0 && index < this.selectedYAxisDisplayNames.length) {
        return this.selectedYAxisDisplayNames[index];
      }
      // Fallback: find in filtered items
      const item = this.filteredYAxisItems.find((item) => item.name === columnName);
      return item ? item.displayName : columnName;
    },
    removeYAxisItem(columnName) {
      const index = this.selectedYAxisKey.indexOf(columnName);
      if (index >= 0) {
        this.selectedYAxisKey.splice(index, 1);
        this.selectedYAxisDisplayNames.splice(index, 1);
        this.handleYSelection(this.selectedYAxisKey);
      }
    },
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard/visualEditsPanel.css';
</style>

<style>
/* Unscoped — must pierce Vuetify v-select internals (scoped CSS cannot reach these nodes) */
.visual-edits-panel .custom-field.v-select .v-select__selection-text,
.visual-edit-sidebar .custom-field.v-select .v-select__selection-text {
  display: none !important;
}

.visual-edits-panel .custom-field.v-select .v-field__input > input,
.visual-edit-sidebar .custom-field.v-select .v-field__input > input {
  position: absolute !important;
  opacity: 0 !important;
  width: 0 !important;
  min-width: 0 !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  pointer-events: none !important;
}
</style>
